import { getV3ApprovalRoute } from '@apps/web/features/phoebe_chat_v3/model/approval_routing';
import {
    type V3ChatEntry,
    type V3CommsApprovalGroupEntry,
    type V3CommsGroupItem,
} from '@apps/web/features/phoebe_chat_v3/model/transcript';

function isGroupableCommsEntry(entry: V3ChatEntry): entry is V3CommsGroupItem {
    return (
        entry.kind === 'tool' &&
        entry.agentTurnId !== null &&
        entry.callId !== null &&
        entry.commsPresentation !== null &&
        getV3ApprovalRoute(entry) === 'communications'
    );
}

function hasPendingApproval(entries: ReadonlyArray<V3CommsGroupItem>): boolean {
    return entries.some(
        (entry) => entry.commsPresentation.phase === 'needs_review',
    );
}

function groupedEntry(
    entries: ReadonlyArray<V3CommsGroupItem>,
    agentTurnId: string,
): V3CommsApprovalGroupEntry {
    const firstEntry = entries[0];
    if (firstEntry === undefined) {
        throw new Error('A communications approval group cannot be empty');
    }
    return {
        kind: 'comms-approval-group',
        id: `comms-approval-group:${firstEntry.id}`,
        createdAt: firstEntry.createdAt,
        agentTurnId,
        items: entries,
        revision: entries
            .map((entry) => `${entry.id}:${entry.revision}`)
            .join('\u001f'),
    };
}

/**
 * Combine communications proposed by the same v3 agent turn while at least
 * one still needs a decision. The source tool rows and call ids stay intact
 * so each decision still uses the normal run-native approval path.
 */
export function groupV3CommsApprovals(
    entries: ReadonlyArray<V3ChatEntry>,
): ReadonlyArray<V3ChatEntry> {
    const candidatesByTurn = new Map<string, Array<V3CommsGroupItem>>();
    for (const entry of entries) {
        if (!isGroupableCommsEntry(entry)) {
            continue;
        }
        const candidates = candidatesByTurn.get(entry.agentTurnId) ?? [];
        candidates.push(entry);
        candidatesByTurn.set(entry.agentTurnId, candidates);
    }

    const groupedTurns = new Set(
        [...candidatesByTurn.entries()]
            .filter(
                ([_agentTurnId, candidates]) =>
                    candidates.length >= 2 && hasPendingApproval(candidates),
            )
            .map(([agentTurnId]) => agentTurnId),
    );
    const emittedTurns = new Set<string>();
    const grouped: Array<V3ChatEntry> = [];
    for (const entry of entries) {
        if (
            !isGroupableCommsEntry(entry) ||
            !groupedTurns.has(entry.agentTurnId)
        ) {
            grouped.push(entry);
            continue;
        }
        if (emittedTurns.has(entry.agentTurnId)) {
            continue;
        }
        emittedTurns.add(entry.agentTurnId);
        grouped.push(
            groupedEntry(
                candidatesByTurn.get(entry.agentTurnId) ?? [],
                entry.agentTurnId,
            ),
        );
    }
    return grouped;
}
